import {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  AccountInterface,
  DestinationInterface,
  LocationInterface,
  RecurrentDestinationInterface,
} from '../../interfaces'
import {
  getFavoriteAndHistoricalLocations,
  getFilteredAccounts,
  mapToDestiInterface,
} from '../../services'
import { formatName } from '../../utils/format'
import { filterDestinationByQuery } from '../../utils/shipmentImportUtils'
import {
  serverQueryStillCovers,
  serverSearchTokens,
  toServerQueryForm,
} from '../../utils/fulltext'
import { FormGroupSearch } from '../FormFields/FormFile'
import { UserPlusIcon } from '@heroicons/react/20/solid'
import { useAppSelector } from '../../store/hooks'

interface AccountSearchProps {
  title: string
  selected?: AccountInterface
  disabled?: boolean
  error?: string
  placeholder?: string
  hideCreate?: boolean
  hideFavAndShip?: boolean
  onChange?: (search: string) => void
  onSelectClient: (option: AccountInterface) => void
  openCreationModal: (open: boolean) => void
}

interface AccountSearchFavoriteHistoricProps extends AccountSearchProps {
  onSelectDestination: (destination: DestinationInterface) => void
}

/** Cuántas filas de cada grupo se ven antes de la fila "y N más". */
const VISIBLE_CAP = 5

type BookState = 'idle' | 'loading' | 'loaded' | 'failed'

/** Fila informativa al pie de un grupo. No es una opción: no se puede elegir. */
const TrailerRow: FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="px-4 py-2 text-xs text-gray-500 italic">{children}</p>
)

/**
 * Fila al pie que sí se pulsa. `onMouseDown` con `preventDefault` porque el
 * desplegable se cierra al perder el foco, y el clic tiene que resolverse antes.
 */
const TrailerButton: FC<{
  label: string
  onClick: () => void
  children: React.ReactNode
}> = ({ label, onClick, children }) => (
  <button
    type="button"
    aria-label={label}
    className="flex w-full items-center px-4 py-2 text-left text-xs text-main-500 hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
    onMouseDown={(event) => event.preventDefault()}
    onClick={onClick}
  >
    {children}
  </button>
)

export const AccountSearchFavoriteHistoric: FC<
  AccountSearchFavoriteHistoricProps
> = ({
  title,
  error,
  selected,
  disabled,
  placeholder,
  hideFavAndShip,
  onSelectClient,
  onSelectDestination,
  openCreationModal,
}) => {
  // Vacío, no un espacio: arrancar con ' ' era una búsqueda al montar.
  const [search, setSearch] = useState('')
  const [loader, setLoader] = useState(false)
  const [clients, setClients] = useState<AccountInterface[]>([])
  const [timerId, setTimerId] = useState<NodeJS.Timeout | null>(null)

  /**
   * La libreta del remitente: se trae una vez por cuenta y el servicio la
   * cachea cinco minutos. `baseState` gobierna todos los estados visibles de
   * los grupos Favoritos y Recientes.
   */
  const [rawRows, setRawRows] = useState<RecurrentDestinationInterface[]>([])
  const [baseState, setBaseState] = useState<BookState>('idle')

  /**
   * Resultado de la búsqueda opcional sobre la libreta completa. Se conserva
   * sólo mientras el texto del campo siga afinando la consulta con la que se
   * trajo.
   */
  const [serverRows, setServerRows] = useState<
    RecurrentDestinationInterface[] | null
  >(null)
  const [serverQuery, setServerQuery] = useState('')
  const [serverState, setServerState] = useState<BookState>('idle')

  // Números de secuencia en vez de la bandera `canceled` por valor de antes,
  // que se leía al llamar y por lo tanto no cancelaba nada: las filas de la
  // cuenta A podían aterrizar bajo la cuenta B.
  const seqRef = useRef(0)
  const serverSeqRef = useRef(0)
  const serverRawQueryRef = useRef('')
  const searchRef = useRef(search)
  const prevFocusedRef = useRef(false)
  const retryArmedRef = useRef(false)

  const clientId = useAppSelector((state) => state.user.client?.id)
  const identificationTypes = useAppSelector(
    (state) => state.inmutable.taxIdentificationTypes
  )
  const businessUnits = useAppSelector((state) => state.inmutable.businessUnits)

  useEffect(() => {
    searchRef.current = search
  }, [search])

  const getClientsData = useCallback(async (criteria: string) => {
    // La comprobación de vacío va primero: `setLoader(true)` antes de un
    // retorno temprano dejaba el grupo Clientes girando para siempre.
    if (criteria.trim() === '') {
      setClients([])
      setLoader(false)
      return
    }

    setLoader(true)
    const clients = await getFilteredAccounts(criteria)
    if (!!clients) {
      setClients(clients)
    }
    setLoader(false)
  }, [])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = event.target.value
    setSearch(inputValue)

    if (inputValue.trim() === '') {
      // De forma síncrona, para que el grupo Clientes desaparezca con la tecla
      // y no un rebote después.
      setClients([])
      setLoader(false)
    } else {
      setLoader(true)
    }
  }

  /**
   * La única consulta de la libreta, siempre con `SearchInput` vacío: esa es la
   * que el servicio cachea, y cuando esto vuelve a correr el campo suele tener
   * el nombre del destinatario ya elegido.
   */
  const getLocations = useCallback(async () => {
    seqRef.current += 1
    serverSeqRef.current += 1
    const seq = seqRef.current

    setRawRows([])
    setServerRows(null)
    setServerQuery('')
    setServerState('idle')

    if (!clientId || hideFavAndShip) {
      setBaseState('idle')
      return
    }

    setBaseState('loading')

    const response = await getFavoriteAndHistoricalLocations(clientId, '')

    if (seq !== seqRef.current) return

    if (!response || response.didError || !response.model) {
      setBaseState('failed')
      retryArmedRef.current = true
      return
    }

    setRawRows(response.model)
    setBaseState('loaded')
    retryArmedRef.current = false
  }, [clientId, hideFavAndShip])

  const dropServerSearch = useCallback(() => {
    serverSeqRef.current += 1
    serverRawQueryRef.current = ''
    setServerRows(null)
    setServerQuery('')
    setServerState('idle')
  }, [])

  const runServerSearch = useCallback(
    async (rawQuery: string) => {
      if (!clientId || hideFavAndShip) return

      const sentQuery = toServerQueryForm(rawQuery)
      // El SP descarta los tokens de un carácter, y sin tokens abandona la
      // búsqueda y devuelve la libreta capada que ya tenemos.
      if (serverSearchTokens(sentQuery).length === 0) return

      serverSeqRef.current += 1
      const serverSeq = serverSeqRef.current
      const seq = seqRef.current

      serverRawQueryRef.current = rawQuery
      setServerQuery(sentQuery)
      setServerRows(null)
      setServerState('loading')

      const response = await getFavoriteAndHistoricalLocations(
        clientId,
        rawQuery
      )

      // Se descarta si ya ganó otra búsqueda, un cambio de cuenta o algo que
      // tiró el resultado (blur, selección, una consulta que ya no la afina).
      if (serverSeq !== serverSeqRef.current || seq !== seqRef.current) return
      if (
        !serverQueryStillCovers(sentQuery, toServerQueryForm(searchRef.current))
      ) {
        return
      }

      if (!response || response.didError || !response.model) {
        setServerRows(null)
        setServerState('failed')
        return
      }

      setServerRows(response.model)
      setServerState('loaded')
    },
    [clientId, hideFavAndShip]
  )

  useEffect(() => {
    setSearch(formatName(selected?.accountFullName ?? ''))
  }, [selected])

  // El grupo Clientes conserva sus disparadores de siempre: es otro endpoint
  // (`/Account?idStr=`) y lo acota su propia rama.
  useEffect(() => {
    if (timerId) {
      clearTimeout(timerId)
    }

    const newTimerId = setTimeout(() => {
      getClientsData(search)
    }, 1000)

    setTimerId(newTimerId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  // La única búsqueda de la libreta. Antes salía al montar, al enfocar, al
  // pulsar Enter, en cada pausa de un segundo, al perder el foco y al elegir
  // una opción: de 10 a 20 ejecuciones del SP por guía.
  useEffect(() => {
    getLocations()

    return () => {
      // Subir la secuencia al desmontar evita que una respuesta tardía toque
      // estado muerto.
      seqRef.current += 1
    }
  }, [getLocations])

  // Conservar o tirar el resultado del servidor a medida que cambia el texto.
  useEffect(() => {
    if (serverState === 'idle') return

    const current = toServerQueryForm(search)

    if (serverState === 'failed') {
      if (current !== serverQuery) dropServerSearch()
      return
    }

    // No es contención: el SP une sus tokens con OR, así que una consulta que
    // gana una palabra es MÁS AMPLIA que la que tenemos, y quedarnos con la
    // respuesta vieja escondería filas que el servidor ya devolvería.
    if (search.trim() === '' || !serverQueryStillCovers(serverQuery, current)) {
      dropServerSearch()
    }
  }, [search, serverState, serverQuery, dropServerSearch])

  /**
   * La base que filtra el desplegable. Con una búsqueda de servidor encima es
   * la unión de los dos conjuntos, sin repetidos: el servidor encuentra por
   * palabra completa (CONTAINS) y este filtro por subcadena, así que su
   * resultado no contiene todo lo que el usuario ya veía.
   */
  const baseRows = useMemo(() => {
    if (!serverRows) return rawRows

    const keyOf = (row: RecurrentDestinationInterface) =>
      `${row.tipoAdr}|${row.consigneeAccountID}|${row.deliveryTypeID}|${row.addressID}`

    const seen = new Set(rawRows.map(keyOf))

    return [
      ...rawRows,
      ...serverRows.filter((row) => {
        const key = keyOf(row)
        if (seen.has(key)) return false
        seen.add(key)
        return true
      }),
    ]
  }, [rawRows, serverRows])

  /**
   * El mapeo vive aquí y no en el servicio porque necesita los catálogos, que
   * se hidratan después del login: así una libreta traída antes de que llegara
   * el catálogo se vuelve a mapear sola, sin otra consulta.
   */
  const mapped = useMemo(
    () =>
      baseRows.map((row) => ({
        row,
        destination: mapToDestiInterface(
          row,
          identificationTypes,
          businessUnits
        ),
      })),
    [baseRows, identificationTypes, businessUnits]
  )

  const favoriteMatches = useMemo(
    () =>
      mapped
        .filter((entry) => entry.row.tipoAdr === 'Favorito')
        .map((entry) => entry.destination)
        .filter((destination) =>
          filterDestination(search, destination.account, destination.location)
        ),
    [mapped, search]
  )

  // Recientes conserva el orden del servidor, que es por fecha.
  const historicMatches = useMemo(
    () =>
      mapped
        .filter((entry) => entry.row.tipoAdr === 'Sales')
        .map((entry) => entry.destination)
        .filter((destination) =>
          filterDestination(search, destination.account, destination.location)
        ),
    [mapped, search]
  )

  const totalMatches = favoriteMatches.length + historicMatches.length
  const bookLoading = baseState === 'loading' || serverState === 'loading'

  /**
   * Un solo predicado para la tecla Enter y para la fila al pie.
   *
   * A propósito NO mira si la libreta vino en su tope. Antes sí: `favoritos >=
   * 50 || recientes >= 50` sobre las filas recibidas. Pero el SP aplica TOP 50
   * en pasos intermedios y luego agrupa y une, así que una libreta truncada
   * llega POR DEBAJO del umbral — medido el 2026-09-13 en Innovus, una cuenta
   * real con al menos 201 destinatarios entrega 49 filas, y la fila que habría
   * alcanzado a los otros 152 no se pintaba nunca.
   *
   * Menos de cinco coincidencias locales es la señal que de verdad importa: el
   * usuario no encuentra al destinatario que tiene delante.
   */
  const canServerSearch =
    baseState === 'loaded' &&
    serverState === 'idle' &&
    serverSearchTokens(toServerQueryForm(search)).length > 0 &&
    totalMatches < VISIBLE_CAP

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return

    // Este campo vive dentro de formularios Formik: Enter no debe enviarlos.
    event.preventDefault()

    // El grupo Clientes mantiene su disparador de siempre.
    getClientsData(search)

    if (serverState === 'failed') {
      runServerSearch(serverRawQueryRef.current)
      return
    }

    if (!canServerSearch) return

    runServerSearch(search)
  }

  const handleChangeFocus = useCallback(
    (focus: boolean) => {
      const wasFocused = prevFocusedRef.current
      prevFocusedRef.current = focus

      if (!focus) {
        setSearch(formatName(selected?.accountFullName ?? ''))
        dropServerSearch()
        return
      }

      // `FormGroupSearch` vuelve a llamar esto en cada render, así que sólo
      // cuenta el flanco real false -> true, y la bandera lo deja en una vez
      // por fallo. Sin las dos cosas, un backend caído entraría en bucle.
      if (!wasFocused && baseState === 'failed' && retryArmedRef.current) {
        retryArmedRef.current = false
        getLocations()
      }
    },
    [baseState, dropServerSearch, getLocations, selected]
  )

  const buildBookTrailer = (matches: number, isFavorites: boolean) => {
    if (bookLoading) return undefined

    if (baseState === 'failed') {
      // Una sola fila de reintento, en Favoritos, para no repetirla dos veces.
      return isFavorites ? (
        <TrailerButton
          label="Reintentar la carga de su libreta"
          onClick={() => getLocations()}
        >
          No se pudo cargar su libreta. Toque para reintentar.
        </TrailerButton>
      ) : undefined
    }

    if (baseState !== 'loaded') return undefined

    if (matches === 0) {
      // Con el campo vacío, un grupo vacío simplemente no se pinta.
      return search.trim() === '' ? undefined : (
        <TrailerRow>Sin coincidencias</TrailerRow>
      )
    }

    if (matches > VISIBLE_CAP) {
      return (
        <TrailerRow>y {matches - VISIBLE_CAP} más, siga escribiendo</TrailerRow>
      )
    }

    if (isFavorites && serverState === 'failed') {
      return (
        <TrailerButton
          label="Reintentar la búsqueda en toda su libreta"
          onClick={() => runServerSearch(serverRawQueryRef.current)}
        >
          No se pudo buscar en su libreta. Toque para reintentar.
        </TrailerButton>
      )
    }

    if (isFavorites && canServerSearch) {
      return (
        <TrailerButton
          label="Buscar en toda su libreta"
          onClick={() => runServerSearch(search)}
        >
          Presione Enter para buscar en toda su libreta
        </TrailerButton>
      )
    }

    return undefined
  }

  const showBookGroups = !hideFavAndShip

  return (
    <div className="flex flex-1 flex-col">
      <div className="relative w-full">
        <FormGroupSearch<Partial<DestinationInterface>>
          clickeable
          value={search}
          id={`search-favorite-historic-account-${title}`}
          label={title}
          error={error}
          disabled={disabled}
          placeholder={placeholder}
          optionGroups={[
            ...(showBookGroups
              ? [
                  {
                    loader: bookLoading,
                    title: 'Favoritos',
                    options: favoriteMatches.slice(0, VISIBLE_CAP),
                    trailer: buildBookTrailer(favoriteMatches.length, true),
                  },
                  {
                    loader: bookLoading,
                    title: 'Recientes',
                    options: historicMatches.slice(0, VISIBLE_CAP),
                    trailer: buildBookTrailer(historicMatches.length, false),
                  },
                ]
              : []),
            {
              loader,
              title: 'Clientes',
              options: clients.map((account) => ({
                account,
              })),
            },
          ]}
          onChange={handleChange}
          onClick={() => {
            // Sólo el grupo Clientes. La libreta ya no se vuelve a pedir al
            // enfocar: era una de las 10-20 llamadas por guía.
            getClientsData(search)
          }}
          onSelectOption={(option) => {
            if (!option) return
            if (!!option.account && !option.location) {
              onSelectClient(option.account)
              setSearch(option.account.accountFullName)
            } else if (!!option.account && !!option.location) {
              setSearch(option.account.accountFullName)
              onSelectDestination(option as DestinationInterface)
            }
            dropServerSearch()
          }}
          onChangeFocus={handleChangeFocus}
          RenderOption={({ option }) => <LocationOption {...option} />}
          LastOption={() => (
            <AddClient onClick={() => openCreationModal(true)} />
          )}
          onKeyDown={handleKeyDown}
          autoComplete="off"
        />
      </div>
    </div>
  )
}

/**
 * Adaptador fino sobre `filterDestinationByQuery`. Ya no mira el nombre ni la
 * dirección de la ubicación: la libreta se filtra por destinatario (nombre,
 * identificación, teléfono, correo), y las direcciones metían coincidencias
 * que el usuario no podía explicar.
 */
export const filterDestination = (
  search: string,
  client?: AccountInterface,
  location?: LocationInterface
) => {
  if (!client) return false

  return filterDestinationByQuery(
    {
      account: client,
      location: location ?? ({} as LocationInterface),
      businessUnit: {} as any,
      isHomeDelivery: false,
    },
    search
  )
}

const LocationOption: FC<Partial<DestinationInterface>> = ({
  account,
  location,
}) => {
  return (
    <div className="flex items-center">
      <div className="flex flex-col w-full">
        <p className="text-sm font-semibold text-main-500 group-hover:text-white leading-5 truncate">
          {account?.displayName}
        </p>
        <p className="text-sm font-semibold leading-5 truncate">
          {account?.accountFullName}
        </p>
        {!account?.listAccountPhone ||
        account?.listAccountPhone.length === 0 ||
        (account?.abbreviationName !== 'V-' &&
          account?.abbreviationName !== 'E-') ? (
          <p className="text-sm leading-5">
            {account?.abbreviationName}
            {account?.identificationNumber}
          </p>
        ) : (
          <p className="text-sm leading-5">
            {!!account?.listAccountPhone &&
              account?.listAccountPhone.length > 0 &&
              account?.listAccountPhone[0].phoneNumber}
          </p>
        )}
        <p className="text-sm leading-5">
          {account?.listAccountEmail[0]?.email ?? ''}
        </p>
        <p className="text-sm leading-5">{location?.name}</p>
        <p className="text-xs leading-5 truncate">
          {location?.address}{' '}
          {!!location?.postalCode
            ? `(código postal: ${location?.postalCode})`
            : ''}
        </p>
      </div>
    </div>
  )
}

const AddClient: FC<{ onClick: React.MouseEventHandler<HTMLDivElement> }> = ({
  onClick,
}) => {
  return (
    <div className="flex flex-1 py-3 items-center opacity-75" onClick={onClick}>
      <UserPlusIcon className="h-5 w-5 mr-2 shrink-0" />

      <p className="text-sm truncate">Crear nuevo</p>
    </div>
  )
}
